import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Globe } from "lucide-react";

// Logo component
export const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_4bb091c1-e4be-474e-9067-19b51e555ba6/artifacts/7q15o4qo_logo.jpeg"
    alt="Murphy's Giving Market"
    className="h-16 lg:h-20 w-auto object-contain"
  />
);

// Navigation component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [newsDropdownOpen, setNewsDropdownOpen] = useState(false);
  const [getHelpDropdownOpen, setGetHelpDropdownOpen] = useState(false);
  const [waysToHelpDropdownOpen, setWaysToHelpDropdownOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Toggle translate dropdown
  const handleTranslateClick = useCallback((e) => {
    e.stopPropagation();
    setTranslateOpen(prev => {
      const newState = !prev;
      if (newState) {
        setAboutDropdownOpen(false);
        setNewsDropdownOpen(false);
        setGetHelpDropdownOpen(false);
        setWaysToHelpDropdownOpen(false);
        
        // Move Google Translate element into dropdown
        const gtElement = document.getElementById('google_translate_element');
        const dropdownContainer = document.getElementById('translate-dropdown-container');
        if (gtElement && dropdownContainer) {
          dropdownContainer.appendChild(gtElement);
          gtElement.style.display = 'block';
        }
      }
      return newState;
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!aboutDropdownOpen && !newsDropdownOpen && !getHelpDropdownOpen && !waysToHelpDropdownOpen && !translateOpen) {
      return;
    }
    
    const handleClickOutside = () => {
      setAboutDropdownOpen(false);
      setNewsDropdownOpen(false);
      setGetHelpDropdownOpen(false);
      setWaysToHelpDropdownOpen(false);
      setTranslateOpen(false);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [aboutDropdownOpen, newsDropdownOpen, getHelpDropdownOpen, waysToHelpDropdownOpen, translateOpen]);

  const aboutSubmenu = [
    { name: 'Mission and Vision', href: '/about/mission-vision' },
    { name: 'Values', href: '/about/values' },
    { name: 'History', href: '/about/history' },
    { name: 'Board', href: '/about/board' },
    { name: 'Staff', href: '/about/staff' },
    { name: 'Non-Discrimination Statement', href: 'https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/grgr3adk_And%20Justice%20for%20All%20Poster.pdf', external: true },
  ];

  const newsSubmenu = [
    { name: 'Media', href: '/news/in-the-news' },
    { name: 'Newsletters', href: '/news/newsletter' },
  ];

  const getHelpSubmenu = [
    { name: 'Food', href: '/get-help/food' },
    { name: 'Partner Agencies', href: '/get-help/partner-agencies' },
    { name: 'Emergency Assistance', href: '/get-help/emergency-assistance' },
    { name: 'Utility/Rental Assistance', href: 'https://customer-assets.emergentagent.com/job_4bb091c1-e4be-474e-9067-19b51e555ba6/artifacts/4667dtt2_MGM2026%20Senior%20Resource%20Guide-Rental%20and%20Utility.pdf', external: true },
    { name: 'Resources (English)', href: '/get-help/resources-english' },
    { name: 'Recursos (español)', href: '/get-help/resources-spanish' },
    { name: 'English Class', href: '/get-help/english-class' },
  ];

  const supportUsSubmenu = [
    { name: 'Financial Support', href: '/ways-to-help/financial-support' },
    { name: 'Items Needed', href: '/ways-to-help/items-needed' },
    { name: 'Volunteer', href: '/ways-to-help/volunteer' },
  ];

  // Determine if nav should be transparent (only on homepage when not scrolled)
  const isTransparent = isHomePage && !isScrolled;

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isTransparent ? 'bg-transparent' : 'nav-glass shadow-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex items-center h-20 md:h-24">
          {/* Logo - always visible, to the left of nav items */}
          <Link 
            to="/" 
            data-testid="logo-link" 
            className="flex-shrink-0 mr-12"
          >
            <div className={`transition-all duration-300 ${isTransparent ? 'bg-white rounded-xl p-2 shadow-lg' : ''}`}>
              <Logo />
            </div>
          </Link>

          {/* Desktop Navigation - spread out */}
          <div className="hidden lg:flex items-center gap-12">
            <Link
              to="/"
              data-testid="nav-home"
              className={`font-medium transition-colors duration-200 text-base tracking-wide ${
                isTransparent 
                  ? 'text-white hover:text-white/80' 
                  : 'text-[#1A1A1A] hover:text-[#C41E3A]'
              }`}
            >
              Home
            </Link>

            {/* About with Dropdown */}
            <div className="relative">
              <button
                data-testid="nav-about"
                onClick={(e) => {
                  e.stopPropagation();
                  setAboutDropdownOpen(!aboutDropdownOpen);
                }}
                className={`font-medium transition-colors duration-200 text-base tracking-wide flex items-center gap-1 ${
                  isTransparent 
                    ? 'text-white hover:text-white/80' 
                    : 'text-[#1A1A1A] hover:text-[#C41E3A]'
                }`}
              >
                About
                <ChevronDown size={16} className={`transition-transform ${aboutDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {aboutDropdownOpen && (
                <div 
                  data-testid="about-dropdown"
                  className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[220px]"
                >
                  {aboutSubmenu.map((item) => (
                    item.external ? (
                      <a
                        key={item.name}
                        href={item.href}
                        data-testid={`nav-about-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="block px-4 py-2 text-[#1A1A1A] hover:bg-[#C41E3A]/10 hover:text-[#C41E3A] transition-colors"
                        onClick={() => setAboutDropdownOpen(false)}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        data-testid={`nav-about-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="block px-4 py-2 text-[#1A1A1A] hover:bg-[#C41E3A]/10 hover:text-[#C41E3A] transition-colors"
                        onClick={() => setAboutDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>

            {/* Get Help with Dropdown */}
            <div className="relative">
              <button
                data-testid="nav-get-help"
                onClick={(e) => {
                  e.stopPropagation();
                  setGetHelpDropdownOpen(!getHelpDropdownOpen);
                  setAboutDropdownOpen(false);
                  setNewsDropdownOpen(false);
                }}
                className={`font-medium transition-colors duration-200 text-base tracking-wide flex items-center gap-1 ${
                  isTransparent 
                    ? 'text-white hover:text-white/80' 
                    : 'text-[#1A1A1A] hover:text-[#C41E3A]'
                }`}
              >
                Get Help
                <ChevronDown size={16} className={`transition-transform ${getHelpDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Get Help Dropdown Menu */}
              {getHelpDropdownOpen && (
                <div 
                  data-testid="get-help-dropdown"
                  className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[220px]"
                >
                  {getHelpSubmenu.map((item) => (
                    item.external ? (
                      <a
                        key={item.name}
                        href={item.href}
                        data-testid={`nav-get-help-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="block px-4 py-2 text-[#1A1A1A] hover:bg-[#C41E3A]/10 hover:text-[#C41E3A] transition-colors"
                        onClick={() => setGetHelpDropdownOpen(false)}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.href}
                        data-testid={`nav-get-help-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className="block px-4 py-2 text-[#1A1A1A] hover:bg-[#C41E3A]/10 hover:text-[#C41E3A] transition-colors"
                        onClick={() => setGetHelpDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>

            {/* Support Us with Dropdown */}
            <div className="relative">
              <button
                data-testid="nav-support-us"
                onClick={(e) => {
                  e.stopPropagation();
                  setWaysToHelpDropdownOpen(!waysToHelpDropdownOpen);
                  setAboutDropdownOpen(false);
                  setGetHelpDropdownOpen(false);
                  setNewsDropdownOpen(false);
                }}
                className={`font-medium transition-colors duration-200 text-base tracking-wide flex items-center gap-1 ${
                  isTransparent 
                    ? 'text-white hover:text-white/80' 
                    : 'text-[#1A1A1A] hover:text-[#C41E3A]'
                }`}
              >
                Support Us
                <ChevronDown size={16} className={`transition-transform ${waysToHelpDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Support Us Dropdown Menu */}
              {waysToHelpDropdownOpen && (
                <div 
                  data-testid="support-us-dropdown"
                  className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[200px]"
                >
                  {supportUsSubmenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`nav-support-us-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="block px-4 py-2 text-[#1A1A1A] hover:bg-[#C41E3A]/10 hover:text-[#C41E3A] transition-colors"
                      onClick={() => setWaysToHelpDropdownOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* News with Dropdown */}
            <div className="relative">
              <button
                data-testid="nav-news"
                onClick={(e) => {
                  e.stopPropagation();
                  setNewsDropdownOpen(!newsDropdownOpen);
                  setAboutDropdownOpen(false);
                  setGetHelpDropdownOpen(false);
                  setWaysToHelpDropdownOpen(false);
                }}
                className={`font-medium transition-colors duration-200 text-base tracking-wide flex items-center gap-1 ${
                  isTransparent 
                    ? 'text-white hover:text-white/80' 
                    : 'text-[#1A1A1A] hover:text-[#C41E3A]'
                }`}
              >
                News
                <ChevronDown size={16} className={`transition-transform ${newsDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* News Dropdown Menu */}
              {newsDropdownOpen && (
                <div 
                  data-testid="news-dropdown"
                  className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-100 py-2 min-w-[180px]"
                >
                  {newsSubmenu.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={`nav-news-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                      className="block px-4 py-2 text-[#1A1A1A] hover:bg-[#C41E3A]/10 hover:text-[#C41E3A] transition-colors"
                      onClick={() => setNewsDropdownOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Sponsors */}
            <Link
              to="/sponsors"
              data-testid="nav-sponsors"
              className={`font-medium transition-colors duration-200 text-base tracking-wide ${
                isTransparent 
                  ? 'text-white hover:text-white/80' 
                  : 'text-[#1A1A1A] hover:text-[#C41E3A]'
              }`}
            >
              Sponsors
            </Link>

            {/* Contact */}
            <Link
              to="/contact"
              data-testid="nav-contact"
              className={`font-medium transition-colors duration-200 text-base tracking-wide ${
                isTransparent 
                  ? 'text-white hover:text-white/80' 
                  : 'text-[#1A1A1A] hover:text-[#C41E3A]'
              }`}
            >
              Contact
            </Link>

            {/* Google Translate */}
            <div className="relative flex items-center">
              <button
                onClick={handleTranslateClick}
                data-testid="nav-translate-btn"
                className="bg-[#1A1A1A] hover:bg-[#333333] text-white p-2.5 rounded-full transition-all duration-200 hover:scale-105 flex items-center justify-center"
                title="Translate"
              >
                <Globe size={20} />
              </button>
              
              {/* Google Translate Widget Dropdown - always in DOM, visibility toggled */}
              <div 
                className={`absolute top-full right-0 mt-2 z-[100] bg-white rounded-lg shadow-lg border border-gray-100 p-4 min-w-[200px] transition-opacity duration-200 ${translateOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'}`}
                onClick={(e) => e.stopPropagation()}
              >
                <p className="text-sm text-gray-600 mb-2 font-medium">Select Language</p>
                <div id="translate-dropdown-container"></div>
              </div>
            </div>

            {/* Donate Button */}
            <a
              href="https://www.zeffy.com/en-US/donation-form/d4b70ccf-7c7a-43d8-a48e-1328d3056f49"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-donate-btn"
              className="bg-[#C41E3A] hover:bg-[#a31830] text-white font-semibold px-6 py-2 rounded-full transition-all duration-200 hover:scale-105"
            >
              Donate
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className={`lg:hidden p-2 ml-auto transition-colors ${isTransparent ? 'text-white' : 'text-[#1A1A1A]'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        data-testid="mobile-menu"
        className={`lg:hidden fixed right-4 top-20 bg-white z-40 transform transition-all duration-300 rounded-xl shadow-lg border border-gray-200 max-h-[80vh] overflow-y-auto ${
          mobileMenuOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
        }`}
        style={{ backgroundColor: '#ffffff' }}
      >
        <div className="flex flex-col p-4 space-y-2">
          <Link
            to="/"
            data-testid="mobile-nav-home"
            className="text-[#1A1A1A] hover:text-[#C41E3A] font-medium text-base py-1.5 border-b border-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Home
          </Link>
          
          {/* About section with submenu */}
          <div className="border-b border-gray-100 pb-2">
            <span className="text-[#1A1A1A] font-medium text-base py-1.5 block">About</span>
            <div className="pl-3 space-y-1">
              {aboutSubmenu.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-about-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-[#666666] hover:text-[#C41E3A] font-medium text-sm py-0.5 block"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Get Help section with submenu */}
          <div className="border-b border-gray-100 pb-2">
            <span className="text-[#1A1A1A] font-medium text-base py-1.5 block">Get Help</span>
            <div className="pl-3 space-y-1">
              {getHelpSubmenu.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    data-testid={`mobile-nav-get-help-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[#666666] hover:text-[#C41E3A] font-medium text-sm py-0.5 block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    data-testid={`mobile-nav-get-help-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-[#666666] hover:text-[#C41E3A] font-medium text-sm py-0.5 block"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>

          {/* Support Us section with submenu */}
          <div className="border-b border-gray-100 pb-2">
            <span className="text-[#1A1A1A] font-medium text-base py-1.5 block">Support Us</span>
            <div className="pl-3 space-y-1">
              {supportUsSubmenu.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-support-us-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-[#666666] hover:text-[#C41E3A] font-medium text-sm py-0.5 block"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* News section with submenu */}
          <div className="border-b border-gray-100 pb-2">
            <span className="text-[#1A1A1A] font-medium text-base py-1.5 block">News</span>
            <div className="pl-3 space-y-1">
              {newsSubmenu.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`mobile-nav-news-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-[#666666] hover:text-[#C41E3A] font-medium text-sm py-0.5 block"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <Link
            to="/sponsors"
            data-testid="mobile-nav-sponsors"
            className="text-[#1A1A1A] hover:text-[#C41E3A] font-medium text-base py-1.5 border-b border-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Sponsors
          </Link>
          <Link
            to="/contact"
            data-testid="mobile-nav-contact"
            className="text-[#1A1A1A] hover:text-[#C41E3A] font-medium text-base py-1.5 border-b border-gray-100"
            onClick={() => setMobileMenuOpen(false)}
          >
            Contact
          </Link>
          <a
            href="https://www.zeffy.com/en-US/donation-form/d4b70ccf-7c7a-43d8-a48e-1328d3056f49"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-donate"
            className="bg-[#C41E3A] hover:bg-[#a31830] text-white font-semibold text-base py-2 px-4 rounded-full text-center mt-2"
            onClick={() => setMobileMenuOpen(false)}
          >
            Donate
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
